import { useState, useMemo, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookingReviewNotice } from './BookingReviewNotice';
import { getCustomerDisplayName } from '../../utils/customer';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import BuildIcon from '@mui/icons-material/Build';
import PauseIcon from '@mui/icons-material/Pause';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShieldIcon from '@mui/icons-material/Shield';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import Tooltip from '@mui/material/Tooltip';
import { statusColors as fallbackStatusColors, neutralGrey } from '@/styles/theme';
import { EmptyState } from '@/components/common/EmptyState';
import { RegistrationPlate, UserAvatar } from '@/components/common';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWorkOrderFlow } from '@/hooks/useWorkOrderFlow';
import {
  DEFAULT_FLOW_STATUS_KEYS,
  DEFAULT_KANBAN_COLUMNS,
  DEFAULT_TERMINAL_STATUS_KEYS,
} from '@/lib/workOrderFlowDefaults';
import {
  OTHER_STATUSES_KEY,
  WAITING_APPROVAL_STATUS,
  approvalWaitLabel,
  showsApprovalWaitMarker,
} from '@/utils/workOrderStatusChips';
import type { WorkOrder, BookingStatus } from '@/types';
import { useOpenWorkOrder, useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';

interface WorkOrdersKanbanViewProps {
  workOrders: WorkOrder[];
  onStatusChange?: (workOrderId: string, newStatus: BookingStatus) => void;
}

// Utility function extracted outside component to avoid recreation
const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

/** Map icon name (from flow config) to MUI icon component */
const ICON_COMPONENTS: Record<string, React.ElementType> = {
  Schedule: ScheduleIcon,
  ThumbUpAlt: ThumbUpAltIcon,
  DirectionsCar: DirectionsCarIcon,
  Build: BuildIcon,
  Pause: PauseIcon,
  LocalShipping: LocalShippingIcon,
  TaskAlt: TaskAltIcon,
  ReceiptLong: ReceiptLongIcon,
  CheckCircle: CheckCircleIcon,
  Cancel: CancelIcon,
};

/** Fallback icon per status — used when flow config is loading */
const FALLBACK_STATUS_ICONS: Record<string, React.ElementType> = {
  pending: HourglassEmptyIcon,
  confirmed: CheckCircleOutlineIcon,
  vehicle_arrived: DirectionsCarIcon,
  in_progress: BuildIcon,
  paused: PauseCircleOutlineIcon,
  waiting_parts: LocalShippingIcon,
  work_completed: AssignmentTurnedInIcon,
  ready_to_invoice: ReceiptLongIcon,
  on_consolidated_invoice: ReceiptLongIcon,
  invoiced: ReceiptLongIcon,
  completed: CheckCircleIcon,
  cancelled: CancelIcon,
  scheduled: HourglassEmptyIcon,
  waiting_approval: ThumbUpAltIcon,
};

function getStatusIconComponent(status: string, statusIcons: Record<string, string>): React.ElementType {
  const iconName = statusIcons[status];
  if (iconName && ICON_COMPONENTS[iconName]) return ICON_COMPONENTS[iconName];
  return FALLBACK_STATUS_ICONS[status] ?? HourglassEmptyIcon;
}

const KanbanCard = memo(({
  workOrder,
  onStatusChange,
  disableStatusTransitions,
}: {
  workOrder: WorkOrder;
  onStatusChange?: (workOrderId: string, newStatus: BookingStatus) => void;
  disableStatusTransitions: boolean;
}) => {
  const navigate = useNavigate();
  const openWorkOrder = useOpenWorkOrder();
  const isViewed = useViewedWorkOrderId() === workOrder.id;
  const { t } = useTranslation('workorders');
  const { getNextStatuses, statusColors } = useWorkOrderFlow();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const rawStatus = workOrder.rawStatus ?? workOrder.status;

  const handleCardClick = useCallback((event?: React.MouseEvent) => {
    openWorkOrder(workOrder.id, event);
  }, [openWorkOrder, workOrder.id]);

  const handleMenuClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleStatusChange = useCallback((newStatus: BookingStatus) => {
    handleMenuClose();
    onStatusChange?.(workOrder.id, newStatus);
  }, [handleMenuClose, onStatusChange, workOrder.id]);

  // Unknown/legacy statuses have no known transition from the other column (#2918).
  // Invoiced and consolidated-invoice attach are billing actions, not card moves.
  // Detach (back to ready_to_invoice) stays in the menu when the flow allows it.
  const nextStatuses = disableStatusTransitions
    ? []
    : getNextStatuses(workOrder.status).filter(
        (s) => s !== 'invoiced' && s !== 'on_consolidated_invoice',
      );

  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        border: 1,
        borderColor: isViewed ? 'primary.main' : 'divider',
        ...(isViewed && {
          bgcolor: 'action.selected',
          boxShadow: (theme) => `inset 4px 0 0 ${theme.palette.primary.main}`,
        }),
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 1,
        },
      }}
      onClick={handleCardClick}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-current={isViewed ? 'true' : undefined}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <BookingReviewNotice required={workOrder.requiresStaffReview} compact />
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" fontWeight={600} color="primary.main">
              {workOrder.workOrderNumber}
            </Typography>
            {workOrder.isDisputed && (
              <Tooltip title={t('workOrderFlags.disputed')}>
                <ReportProblemIcon sx={{ fontSize: 14, color: 'error.main' }} />
              </Tooltip>
            )}
            {workOrder.isInvoicingHold && (
              <Tooltip title={t('workOrderFlags.invoicingHold')}>
                <HourglassBottomIcon sx={{ fontSize: 14, color: 'warning.dark' }} />
              </Tooltip>
            )}
            {workOrder.isWarranty && (
              <Tooltip title={t('workOrderFlags.warranty')}>
                <ShieldIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              </Tooltip>
            )}
            {workOrder.isWaitingForParts && (
              <Tooltip title={t('workOrderFlags.waitingForParts')}>
                <LocalShippingIcon sx={{ fontSize: 14, color: 'warning.main' }} />
              </Tooltip>
            )}
            {/* Derived approval wait (#3027): beside the column's status, never a column of its own. */}
            {showsApprovalWaitMarker(workOrder) && (
              <Tooltip
                title={approvalWaitLabel(
                  t(`status.${WAITING_APPROVAL_STATUS}`),
                  workOrder.openApprovalCount,
                )}
              >
                <PendingActionsIcon sx={{ fontSize: 14, color: 'info.main' }} />
              </Tooltip>
            )}
            {disableStatusTransitions && (
              <Tooltip title={t('unknownStatusTooltip')}>
                <Chip label={rawStatus} size="small" variant="outlined" />
              </Tooltip>
            )}
          </Box>
          <Tooltip title={t('moreOptions')}>
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{ mt: -0.5, mr: -0.5, minWidth: 44, minHeight: 44 }}
              aria-label={t('moreOptions')}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { handleMenuClose(); openWorkOrder(workOrder.id); }}>
              <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
              {t('kanban.open')}
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate(`/workorders/${workOrder.id}?edit=true`); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              {t('kanban.edit')}
            </MenuItem>
            {nextStatuses.map((nextStatus) => (
              <MenuItem key={nextStatus} onClick={() => handleStatusChange(nextStatus as BookingStatus)}>
                <ListItemIcon>
                  <ArrowForwardIcon fontSize="small" sx={{ color: statusColors[nextStatus] ?? neutralGrey }} />
                </ListItemIcon>
                {t(`status.${nextStatus}`)}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Vehicle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <DirectionsCarIcon fontSize="small" color="action" />
          <RegistrationPlate registrationNumber={workOrder.vehicle.registrationNumber} sx={{ height: 22 }} />
          <Typography variant="caption" color="text.secondary" noWrap>
            {workOrder.vehicle.make} {workOrder.vehicle.model}
          </Typography>
        </Box>

        {/* Services */}
        <Box sx={{ mb: 1.5 }}>
          {workOrder.services.slice(0, 2).map((service) => (
            <Typography
              key={service.id}
              variant="body2"
              sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}
            >
              {service.name}
            </Typography>
          ))}
          {workOrder.services.length > 2 && (
            <Typography variant="caption" color="text.secondary">
              {t('kanban.moreServices', { count: workOrder.services.length - 2 })}
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Customer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UserAvatar
              firstName={workOrder.customer.firstName}
              lastName={workOrder.customer.lastName}
              size={24}
              variant="rounded"
              colorScheme={workOrder.customer.customerType === 'business' ? 'business' : 'default'}
            />
            <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
              {getCustomerDisplayName(workOrder.customer)}
            </Typography>
          </Box>

          {/* Price */}
          <Typography variant="body2" fontWeight={600}>
            {formatPrice(workOrder.totalCents)}
          </Typography>
        </Box>

        {/* Mechanic */}
        {workOrder.mechanic && (
          <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {workOrder.mechanic.firstName} {workOrder.mechanic.lastName}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

KanbanCard.displayName = 'KanbanCard';

export function WorkOrdersKanbanView({ workOrders, onStatusChange }: WorkOrdersKanbanViewProps) {
  const { t } = useTranslation('workorders');
  const isMobile = useIsMobile();
  const { kanbanColumns, statuses, statusColors, statusIcons } = useWorkOrderFlow();

  // Statuses the flow knows. A known status without a column (e.g. a terminal
  // status the tenant left off the board) stays off the board as before; only
  // keys nobody knows — legacy `paid`, `confirmed` — go to the other column (#2918).
  const knownStatusKeys = useMemo(
    () => new Set<string>([
      ...statuses.map((s) => s.key),
      ...DEFAULT_FLOW_STATUS_KEYS,
      ...DEFAULT_TERMINAL_STATUS_KEYS,
    ]),
    [statuses],
  );
  const [mobileStatus, setMobileStatus] = useState<string>('in_progress');

  // Build column definitions from flow config
  const columnDefs = useMemo(() => {
    if (kanbanColumns.length === 0) {
      // Fallback while loading
      return DEFAULT_KANBAN_COLUMNS.map((status) => ({
        status: status as BookingStatus,
        color: fallbackStatusColors[status as keyof typeof fallbackStatusColors] ?? neutralGrey,
      }));
    }
    return kanbanColumns.map((status) => ({
      status: status as BookingStatus,
      color: statusColors[status] ?? neutralGrey,
    }));
  }, [kanbanColumns, statusColors]);

  const workOrdersByStatus = useMemo(() => {
    const grouped: Record<string, WorkOrder[]> = {};
    const columnKeys = new Set<string>(columnDefs.map((col) => col.status));
    for (const col of columnDefs) {
      grouped[col.status] = [];
    }
    grouped[OTHER_STATUSES_KEY] = [];

    workOrders.forEach((wo) => {
      const key = wo.rawStatus ?? wo.status;
      if (columnKeys.has(key)) {
        grouped[key]?.push(wo);
      } else if (!knownStatusKeys.has(key)) {
        grouped[OTHER_STATUSES_KEY]?.push(wo);
      }
    });

    return grouped;
  }, [workOrders, columnDefs, knownStatusKeys]);

  const otherWorkOrders = workOrdersByStatus[OTHER_STATUSES_KEY] ?? [];
  const showOtherColumn = otherWorkOrders.length > 0;
  const otherStatusesLabel = useMemo(
    () => [...new Set(otherWorkOrders.map((wo) => wo.rawStatus ?? wo.status))].join(', '),
    [otherWorkOrders],
  );

  const visibleColumns = useMemo(
    () => [
      ...columnDefs.map((col) => ({ ...col, isOther: false })),
      ...(showOtherColumn
        ? [{ status: OTHER_STATUSES_KEY, color: neutralGrey, isOther: true }]
        : []),
    ],
    [columnDefs, showOtherColumn],
  );

  const columnTitle = (column: { status: string; isOther: boolean }) => (
    column.isOther
      ? t('filters.otherStatusesTooltip', { statuses: otherStatusesLabel })
      : t(`status.${column.status}`)
  );

  if (isMobile) {
    return (
      <Box>
        <Tabs
          value={mobileStatus}
          onChange={(_e, newValue) => setMobileStatus(newValue as string)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {visibleColumns.map((column) => (
            <Tab
              key={column.status}
              value={column.status}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: column.color,
                    }}
                  />
                  {columnTitle(column)}
                  <Chip
                    label={(workOrdersByStatus[column.status] ?? []).length}
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, ml: 0.5 }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>

        <Box sx={{ px: 0.5 }}>
          {(workOrdersByStatus[mobileStatus] ?? []).length === 0 ? (
            <EmptyState
              icon={<AssignmentIcon />}
              title={t('noWorkOrdersInStatus')}
              minHeight={200}
            />
          ) : (
            (workOrdersByStatus[mobileStatus] ?? []).map((workOrder) => (
              <KanbanCard
                key={workOrder.id}
                workOrder={workOrder}
                disableStatusTransitions={mobileStatus === OTHER_STATUSES_KEY}
                {...(onStatusChange ? { onStatusChange } : {})}
              />
            ))
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        pb: 2,
        minHeight: 'calc(100vh - 300px)',
      }}
    >
      {visibleColumns.map((column) => (
        <Paper
          key={column.status}
          elevation={0}
          data-testid={`kanban-column-${column.status}`}
          sx={{
            minWidth: 280,
            flex: 1,
            maxWidth: 400,
            backgroundColor: 'background.default',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Column Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {(() => {
              const IconComp = getStatusIconComponent(column.status, statusIcons);
              return <IconComp sx={{ fontSize: 18, color: column.color }} />;
            })()}
            <Typography variant="subtitle2" fontWeight={600}>
              {columnTitle(column)}
            </Typography>
            <Chip
              label={(workOrdersByStatus[column.status] ?? []).length}
              size="small"
              sx={{
                ml: 'auto',
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
            />
          </Box>

          {/* Column Content */}
          <Box
            sx={{
              p: 1.5,
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {(workOrdersByStatus[column.status] ?? []).length === 0 ? (
              <EmptyState
                icon={<AssignmentIcon />}
                title={t('noWorkOrdersInStatus')}
                minHeight={200}
              />
            ) : (
              (workOrdersByStatus[column.status] ?? []).map((workOrder) => (
                <KanbanCard
                  key={workOrder.id}
                  workOrder={workOrder}
                  disableStatusTransitions={column.isOther}
                  {...(onStatusChange ? { onStatusChange } : {})}
                />
              ))
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}
